import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'antd';
import { HeaderComponet } from '../summaryComponents/HeaderComponet';
import { CheckboxComponent } from '../summaryComponents/CheckboxComponent';
import { CheckboxGroupComponent } from '../summaryComponents/CheckboxGroupComponent';
import { ImageComponent } from '../summaryComponents/ImageComponent';
import { SimpleListComponent } from '../summaryComponents/SimpleListComponet';
import { MultipleListComponent } from '../summaryComponents/MultipleListComponent';
import { BarCodeComponents } from '../summaryComponents/BarCodeComponent';
import { SignatureComponent } from '../summaryComponents/SignatureComponent';
import { TextAreaComponent } from '../summaryComponents/TextAreaComponent';
import { RadioComponent } from '../summaryComponents/RadioComponent';
import { LoopComponent } from '../summaryComponents/LoopComponent';
import { AudioComponent } from '../summaryComponents/AudioComponent';
import { ActionComponent } from '../summaryComponents/ActionComponent';
import { TextComponents } from '../summaryComponents/TextComponents';
import { EditSummaryTextField } from '../summaryComponents/editSummaryComponents/EditSummaryTextField';
import { EditSummaryTextAreaField } from '../summaryComponents/editSummaryComponents/EditSummaryTextAreaField';
import { EditSummaryRadioField } from '../summaryComponents/editSummaryComponents/EditSummaryRadioField';
import { EditSummaryMultipleListItem } from '../summaryComponents/editSummaryComponents/EditSummaryMultipleListItem';
import MobileFormContext from '../../../../components/mobileForm/context/mobileFormContext';
import AssetComponent from '../summaryComponents/AssetComponent';
import { AttachmentsComponent } from '../summaryComponents/AttachmentsComponent';
import { EditSummaryAttachmentItem } from '../summaryComponents/editSummaryComponents/EditSummaryAttachmentItem';
import FinalCustomerComponent from '../summaryComponents/FinalCustomerComponent';
import { mobileFormFieldsTypes } from '../../../../config/constants/mobileFormConstant';

const COMPONENT_MAP = {
  header: { render: HeaderComponet, text: 'header' },
  checkbox: { render: CheckboxComponent },
  checkboxGroup: { render: CheckboxGroupComponent },
  imgList: { render: ImageComponent },
  list: { render: SimpleListComponent, renderEdit: EditSummaryTextField },
  listMultiple: { render: MultipleListComponent, renderEdit: EditSummaryMultipleListItem },
  barcodeScan: { render: BarCodeComponents },
  signature: { render: SignatureComponent },
  textArea: { render: TextAreaComponent, renderEdit: EditSummaryTextAreaField },
  radios: { render: RadioComponent, renderEdit: EditSummaryRadioField },
  loop: { render: LoopComponent },
  audio: { render: AudioComponent },
  locationDate: { render: ActionComponent },
  text: { render: TextComponents, renderEdit: EditSummaryTextField },
  asset: { render: AssetComponent },
  attachments: {
    render: AttachmentsComponent,
    renderEdit: EditSummaryAttachmentItem,
  },
  finalCustomer: { render: FinalCustomerComponent },
};

const blockComponents = new Set([
  mobileFormFieldsTypes.header.value,
  mobileFormFieldsTypes.loop.value,
  mobileFormFieldsTypes.radios.value,
  mobileFormFieldsTypes.checkboxGroup.value,
  mobileFormFieldsTypes.textArea.value,
  mobileFormFieldsTypes.checkbox.value,
  mobileFormFieldsTypes.listMultiple.value,
  mobileFormFieldsTypes.imgList.value,
  mobileFormFieldsTypes.attachments.value,
]);

const EditableFormItem = ({ field, parents, onFinishEdit, updateFieldValue }) => {
  const fieldData = COMPONENT_MAP[field.type];

  const onFinish = async (result) => {
    const hasToSave = result?.save;
    if (result?.save) {
      await updateFieldValue({
        field: {
          ...field,
          ...result.newValue,
        },
        parents,
      });
    }
    onFinishEdit({ saved: hasToSave });
  };

  return fieldData.renderEdit({ field, onFinish });
};

const FormItem = ({ field, renderFields, parents, reloadData, readOnly, updateFieldValue, isUpdateAfterFinish }) => {
  const [editing, setEditing] = useState(false);
  const fieldData = COMPONENT_MAP[field.type];

  const handleEditItem = () => {
    setEditing(true);
  };

  const handleFinisEditItem = (result) => {
    if (result?.saved) {
      reloadData();
    }
    setEditing(false);
  };

  if (fieldData == null) {
    return null;
  }

  if (editing && !readOnly) {
    return (
      <EditableFormItem
        field={field}
        parents={parents}
        onFinishEdit={handleFinisEditItem}
        updateFieldValue={updateFieldValue}
      />
    );
  }

  return (
    <fieldData.render
      field={field}
      parents={parents}
      renderFields={renderFields}
      readOnly={readOnly}
      disableEdit={readOnly}
      reloadData={reloadData}
      onFinishEdit={handleFinisEditItem}
      onEdit={fieldData.renderEdit != null && !readOnly && isUpdateAfterFinish ? handleEditItem : null}
    />
  );
};

export const MobileForm = ({ fields, parents = [], reloadData, readOnly, updateFieldValue, columns = 1 }) => {
  const mobileFormContext = useContext(MobileFormContext);
  return (
    <Row gutter={[33, 16]}>
      {fields?.map((field) => {
        if (!COMPONENT_MAP[field.type]) return null;
        return (
          <Col xs={24} md={blockComponents.has(field.type) ? 24 : 24 / columns} key={field.idInput}>
            <FormItem
              field={field}
              renderFields={MobileForm}
              parents={parents}
              reloadData={reloadData}
              readOnly={readOnly}
              updateFieldValue={mobileFormContext?.updateMobileFormField ?? updateFieldValue}
              isUpdateAfterFinish={mobileFormContext?.isUpdateAfterFinish}
            />
          </Col>
        );
      })}
    </Row>
  );
};

MobileForm.propTypes = {
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      idInput: PropTypes.string,
    }),
  ),
  parents: PropTypes.array,
  reloadData: PropTypes.func,
  readOnly: PropTypes.bool,
  updateFieldValue: PropTypes.func,
    columns: PropTypes.number,
};

FormItem.propTypes = {
  field: PropTypes.arrayOf(
    PropTypes.shape({
      idInput: PropTypes.string,
    }),
  ),
  parents: PropTypes.array,
  renderFields: PropTypes.func,
  reloadData: PropTypes.func,
  readOnly: PropTypes.bool,
  updateFieldValue: PropTypes.func,
  isUpdateAfterFinish: PropTypes.bool,
};
